export interface BillingInfo {
  subscriptionStatus: string;
  trialDaysRemaining: number;
  trialStartedAt?: string;
  trialEndsAt?: string;
  stripeCustomerId?: string;
  subscription?: {
    id: string;
    status: string;
    currentPeriodEnd: number;
    currentPeriodStart: number;
    cancelAtPeriodEnd: boolean;
    priceId?: string;
    amount?: number; // Monthly price in pence/cents
    currency?: string; // e.g., 'gbp'
    interval?: string; // 'month' or 'year'
    lastPayment?: {
      date: number; // Unix timestamp
      amount: number; // Amount paid in pence/cents
      currency: string;
      invoiceNumber: string;
      invoiceUrl: string;
    };
  };
}

export interface CheckoutSessionResponse {
  url: string;
  paymentLinkId: string;
}

class BillingService {
  async getPaymentLink(userId: number, planId?: string): Promise<CheckoutSessionResponse> {
    const url = planId ? 
      `/api/billing/payment-link?plan=${encodeURIComponent(planId)}` : 
      '/api/billing/payment-link';
      
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get payment link');
    }

    return response.json();
  }

  async getBillingInfo(userId: number): Promise<BillingInfo> {
    const response = await fetch('/api/billing/info', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch billing information');
    }

    return response.json();
  }

  async cancelSubscription(userId: number, cancellationReason?: string): Promise<{ message: string; cancelAtPeriodEnd: boolean; currentPeriodEnd: number }> {
    const response = await fetch('/api/billing/cancel-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ cancellationReason })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to cancel subscription');
    }

    return response.json();
  }

  // Redirect to Stripe payment link
  async redirectToCheckout(userId: number, planId?: string): Promise<void> {
    try {
      const paymentLink = await this.getPaymentLink(userId, planId);
      
      if (paymentLink.url) {
        // Use top-level redirect to break out of Replit iframe and go to Stripe
        // This allows Stripe to redirect back properly after payment
        if (window.top) {
          window.top.location.href = paymentLink.url;
        } else {
          window.location.href = paymentLink.url;
        }
      } else {
        throw new Error('No payment URL received from server');
      }
    } catch (error) {
      console.error('Error redirecting to payment:', error);
      throw error;
    }
  }
}

export const billingService = new BillingService();